import classNames from "classnames";
import React, { useContext, useRef, useState, forwardRef, useCallback, useImperativeHandle, useEffect } from "react";
import { getParentOfType } from "mobx-state-tree";
import { observer } from "mobx-react";
import { TileLayoutModelType, TileRowModelType } from "../../models/document/tile-row";
import { getTileContentInfo } from "../../models/tiles/tile-content-info";
import { ITileModel } from "../../models/tiles/tile-model";
import { SectionHeader } from "../tiles/section-header";
import { TileApiInterfaceContext } from "../tiles/tile-api";
import { TileComponent, dragTileSrcDocId } from "../tiles/tile-component";
import { useStores } from "../../hooks/use-stores";
import { DocumentContentModel } from "../../models/document/document-content";

import resizeDragIcon from "../../assets/resize-drag-icon.png";

import "./tile-row.scss";

export const kDragResizeRowId = "org.concord.clue.row-resize.id";
// allows source compatibility to be checked in dragOver
export const dragResizeRowId = (id: string) => `org.concord.clue.row-resize.id.${id}`;
export const dragResizeRowY =
              (y: number) => `org.concord.clue.row-resize.event-y.${y}`;
export const dragResizeRowModelHeight =
              (modelHeight: number) => `org.concord.clue.row-resize.model-height.${modelHeight}`;
export const dragResizeRowDomHeight =
              (domHeight: number) => `org.concord.clue.row-resize.dom-height.${domHeight}`;

export function extractDragResizeRowId(dataTransfer: DataTransfer) {
  // get the actual rowId from contents if possible (e.g. on drop)
  const dragRowId = dataTransfer.getData(kDragResizeRowId);
  if (dragRowId) return dragRowId;

  // if not, extract the toLowerCase() version from the key (e.g. on over)
  for (const type of dataTransfer.types) {
    const result = /org\.concord\.clue\.row-resize\.id\.(.*)$/.exec(type);
    if (result) return result[1];
  }
}

export function extractDragResizeY(dataTransfer: DataTransfer) {
  for (const type of dataTransfer.types) {
    const result = /org\.concord\.clue\.row-resize\.event-y\.(.*)$/.exec(type);
    if (result) return +result[1];
  }
}

export function extractDragResizeModelHeight(dataTransfer: DataTransfer) {
  for (const type of dataTransfer.types) {
    const result = /org\.concord\.clue\.row-resize\.model-height\.(.*)$/.exec(type);
    if (result) return +result[1];
  }
}

export function extractDragResizeDomHeight(dataTransfer: DataTransfer) {
  for (const type of dataTransfer.types) {
    const result = /org\.concord\.clue\.row-resize\.dom-height\.(.*)$/.exec(type);
    if (result) return +result[1];
  }
}

interface IProps {
  context: string;
  documentId?: string;  // permanent id (key) of the containing document
  docId: string;  // ephemeral contentId for the DocumentContent
  documentContent: HTMLElement | null;
  typeClass?: string;
  scale?: number;
  model: TileRowModelType;
  rowIndex: number;
  height?: number;
  readOnly?: boolean;
  dropHighlight?: string;
  dropTileInsertIndex?: number;
}

interface IState {
  tileAcceptDrop?: string;
}

export interface TileRowHandle {
  id: string;
  tileRowDiv: HTMLDivElement | null;
  hasTile: (tileId?: string) => boolean;
}

const TileRowComponent = forwardRef<TileRowHandle, IProps>((props, ref) => {
  const { model, typeClass, height: propHeight, readOnly, dropHighlight, dropTileInsertIndex } = props;
  const stores = useStores();
  const [state, setState] = useState<IState>({});
  const tileRowDiv = useRef<HTMLDivElement | null>(null);
  const tileApiInterface = useContext(TileApiInterfaceContext);
  const isSectionHeader = model.isSectionHeader;
  const { sectionId } = model;

  const documentContentModel = getParentOfType(model, DocumentContentModel);
  const tileMap = documentContentModel?.tileMap;

  // Image to use as the image dragged when user operates the resize handle.
  // Note we used to use a transparent pixel, but in the Chromebook browser
  // this resulted in an immediate dragEnd when the image was set.
  const dragImage = useRef<HTMLImageElement>(document.createElement("img"));
  useEffect(() => {
    dragImage.current.src = resizeDragIcon;
  }, []);

  if (!tileMap) {
    throw new Error("Tile map not found");
  }

  // Expose the required methods and properties to the parent component
  useImperativeHandle(ref, () => ({
    id: model.id,
    get tileRowDiv() {
      return tileRowDiv.current;
    },
    hasTile(tileId?: string) {
      return tileId ? model.hasTile(tileId) : false;
    }
  }), [model]);

  const getTile = useCallback((tileId: string) => {
    return tileMap.get(tileId) as ITileModel | undefined;
  }, [tileMap]);

  const isTileRenderable = useCallback((tileId: string) => {
    const tile = getTile(tileId);
    return !!tile && (!tile.display || stores.isShowingTeacherContent);
  }, [getTile, stores.isShowingTeacherContent]);

  const getTileWidth = useCallback((tileId: string, tiles: TileLayoutModelType[]) => {
    return 100 / (tiles.length || 1);
  }, []);

  const getContentHeight = useCallback(() => {
    return model.getContentHeight((tileId: string) => {
      const tileApi = tileApiInterface?.getTileApi(tileId);
      const contentHeight = tileApi?.getContentHeight?.();
      if (contentHeight) return contentHeight;
      // otherwise, use the default height for this type of tile
      const tile = getTile(tileId);
      const tileType = tile?.content.type;
      const contentInfo = getTileContentInfo(tileType);
      if (contentInfo?.defaultHeight) return contentInfo.defaultHeight;
    });
  }, [model, tileApiInterface, getTile]);

  const handleSetCanAcceptDrop = useCallback((tileId?: string) => {
    setState({ tileAcceptDrop: tileId });
  }, []);

  const handleRequestRowHeight = useCallback((tileId: string, ht?: number, deltaHeight?: number) => {
    const { height, tileCount, setRowHeightWithoutUndo } = model;
    const newHeight = ht != null && deltaHeight != null
                        ? ht + deltaHeight
                        : ht;
    // don't shrink the height of a multi-tile row based on a request from a single tile
    if ((tileCount > 1) && (height != null) && (newHeight != null) && (newHeight < height)) return;
    (newHeight != null) && setRowHeightWithoutUndo(newHeight);
  }, [model]);

  const handleStartResizeRow = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    const { docId } = props;
    const { id } = model;

    e.dataTransfer.setDragImage(dragImage.current, 0, 0);
    e.dataTransfer.setData(dragTileSrcDocId(docId), docId);
    e.dataTransfer.setData(kDragResizeRowId, id);
    e.dataTransfer.setData(dragResizeRowId(id), id);
    e.dataTransfer.setData(dragResizeRowY(e.clientY), String(e.clientY));
    if (model.height) {
      e.dataTransfer.setData(dragResizeRowModelHeight(model.height), String(model.height));
    }
    if (tileRowDiv.current) {
      const boundingBox = tileRowDiv.current.getBoundingClientRect();
      e.dataTransfer.setData(dragResizeRowDomHeight(boundingBox.height), String(boundingBox.height));
    }
  }, [props, model, dragImage]);

  const renderTiles = useCallback((tileRefs: TileLayoutModelType[], tileHeight?: number) => {
    const { docId, documentContent, scale, documentId } = props;
    return tileRefs.map((tileRef, index) => {
      const tileModel = getTile(tileRef.tileId);
      const tileWidthPct = getTileWidth(tileRef.tileId, tileRefs);
      return tileModel
        ? <TileComponent
            key={tileModel.id}
            indexInRow={index}
            model={tileModel}
            widthPct={tileWidthPct}
            height={tileHeight}
            isUserResizable={!readOnly && model.isUserResizable}
            onResizeRow={handleStartResizeRow}
            onSetCanAcceptDrop={handleSetCanAcceptDrop}
            onRequestRowHeight={handleRequestRowHeight}
            documentId={documentId}
            docId={docId}
            documentContent={documentContent}
            scale={scale}
            readOnly={readOnly}
            context={props.context}
        />
        : null;
    });
  }, [props, getTile, getTileWidth, readOnly, model.isUserResizable,
    handleStartResizeRow, handleSetCanAcceptDrop, handleRequestRowHeight]);

  const renderDragDropHandles = useCallback(() => {
    const { isUserResizable } = model;
    const { rowIndex } = props;
    const highlight = state.tileAcceptDrop ? undefined : dropHighlight;
    const allZonesVisible = stores.ui.isTilePickedUp;

    const showTopHighlight = (highlight === "top") && (!isSectionHeader || (rowIndex > 0));
    const showBottomHighlight = (highlight === "bottom");

    // During pick-up, show all drop zones dimmed; the active one gets full highlight
    const dimTop = allZonesVisible && !showTopHighlight && (!isSectionHeader || (rowIndex > 0));
    const dimBottom = allZonesVisible && !showBottomHighlight;

    const topClass = `drop-feedback top ${showTopHighlight ? "show" : dimTop ? "show dimmed" : ""}`;
    const bottomClass = `drop-feedback bottom ${showBottomHighlight ? "show" : dimBottom ? "show dimmed" : ""}`;

    // Build ARIA props for drop zones when in pick-up mode.
    const rowLabel = isSectionHeader ? `section ${sectionId || ""}` : `row ${rowIndex + 1}`;
    const ariaProps = (location: string, isVisible: boolean) =>
      allZonesVisible && isVisible
        ? { role: "img" as const, "aria-label": `Insert ${location.toLowerCase()} ${rowLabel}` }
        : {};

    // Tile-boundary side drop zones: N+1 zones for N tiles, positioned at each tile boundary.
    // These replace the old separate "left" and "right" zones.
    const tileBoundaryZones: React.ReactElement[] = [];
    const renderableTileCount = model.tiles.filter(t => isTileRenderable(t.tileId)).length;
    if (renderableTileCount > 0 && !isSectionHeader) {
      const boundaryCount = renderableTileCount + 1;
      for (let b = 0; b < boundaryCount; b++) {
        const isActive = (highlight === "left") && (dropTileInsertIndex === b);
        const isDimmed = allZonesVisible && !isActive;
        const isVisible = isActive || isDimmed;
        const zoneClass = classNames("drop-feedback", "tile-boundary", {
          show: isVisible,
          dimmed: isDimmed
        });

        // Position: first zone at left edge, last at right edge, interior at actual tile boundaries
        let zoneStyle: React.CSSProperties;
        if (b === 0) {
          zoneStyle = { left: 0 };
        } else if (b === boundaryCount - 1) {
          zoneStyle = { right: 0 };
        } else {
          // Read actual tile positions from the DOM to handle unequal-width tiles
          const rowEl = tileRowDiv.current;
          const tileElements = rowEl?.querySelectorAll(':scope > .tool-tile');
          if (tileElements && tileElements.length > b) {
            const leftTileRight = tileElements[b - 1].getBoundingClientRect().right;
            const rightTileLeft = tileElements[b].getBoundingClientRect().left;
            const midpoint = (leftTileRight + rightTileLeft) / 2;
            const rowLeft = rowEl!.getBoundingClientRect().left;
            zoneStyle = { left: `${midpoint - rowLeft - 8}px` };
          } else {
            // Fallback to even split
            const leftPct = (b / renderableTileCount) * 100;
            zoneStyle = { left: `calc(${leftPct}% - 8px)` };
          }
        }

        const label = b === 0
          ? "before first tile in"
          : b === boundaryCount - 1
            ? "after last tile in"
            : `at position ${b} in`;
        tileBoundaryZones.push(
          <div key={`tile-boundary-${b}`} className={zoneClass} style={zoneStyle}
            {...ariaProps(label, isVisible)} />
        );
      }
    }

    return [
      <div key="top-drop-feedback" className={topClass}
        {...ariaProps("above", dimTop || showTopHighlight)} />,
      ...tileBoundaryZones,
      <div key="bottom-drop-feedback" className={bottomClass}
        {...ariaProps("below", dimBottom || showBottomHighlight)} />,
      <div key="bottom-resize-handle"
        className={`bottom-resize-handle ${isUserResizable ? "enable" : "disable"}`}
        draggable={isUserResizable}
        onDragStart={isUserResizable ? handleStartResizeRow : undefined} />
    ];
  }, [model, props, state.tileAcceptDrop, dropHighlight, dropTileInsertIndex, isSectionHeader,
    sectionId, stores.ui.isTilePickedUp, isTileRenderable, handleStartResizeRow]);

  const { tiles: modelTiles } = model;
  const rowHeight = !isSectionHeader
                    ? propHeight || model.height || getContentHeight()
                    : undefined;
  const style = rowHeight ? { height: rowHeight } : undefined;
  const renderableTiles = modelTiles?.filter(tile => isTileRenderable(tile.tileId));
  const hasTeacherTiles = modelTiles.some(tile => getTile(tile.tileId)?.display === "teacher");
  const classes = classNames("tile-row", { "has-teacher-tiles": hasTeacherTiles });
  if (!isSectionHeader && !renderableTiles.length) return null;

  return (
    <div className={classes} data-row-id={model.id}
        style={style} ref={tileRowDiv}>
      { isSectionHeader && sectionId
        ? <SectionHeader type={sectionId} typeClass={typeClass}/>
        : renderTiles(renderableTiles, rowHeight)
      }
      {!readOnly && renderDragDropHandles()}
    </div>
  );
});

TileRowComponent.displayName = "TileRowComponent";

export default observer(TileRowComponent);
